'use client'

import cx from 'classix'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import {
	type CSSProperties,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react'
import { applyStylesIf } from 'utils/applyStylesIf/applyStylesIf'
import styles from './CapsuleAndFamilyShopAnimation.module.css'

interface CapsuleAndFamilyShopAnimationProps {
	readonly content: ReactNode
	readonly inverse: boolean
	readonly fixedContentClassName?: string
}

const ONE_PERCENT = 0.01
const ONE_HUNDRED = 100
const ONE_HUNDRED_ONE = 101

function calculateOpacity(visibleHeight: number, divHeight: number) {
	return visibleHeight / divHeight
}

export function CapsuleAndFamilyShopAnimation({
	content,
	inverse,
	fixedContentClassName,
}: CapsuleAndFamilyShopAnimationProps): ReactNode {
	const ref = useRef<HTMLDivElement>(null)
	const refEndBanner = useRef<HTMLDivElement>(null)
	const refText = useRef<HTMLDivElement>(null)
	const refTextPlaceHolder = useRef<HTMLDivElement>(null)
	const entryBanner = useIntersectionObserver(ref, {
		threshold: ONE_PERCENT,
	})
	const entryEndBanner = useIntersectionObserver(refEndBanner, {
		threshold: 1,
	})
	const entryEndText = useIntersectionObserver(refTextPlaceHolder, {
		threshold: 1,
	})
	const [opacity, setOpacity] = useState<number>(0)

	const isIntersectingBanner = entryBanner?.isIntersecting
	const isIntersectingEndTextBanner = entryEndText?.isIntersecting
	const isIntersectingEndBanner = entryEndBanner?.isIntersecting

	const isFixed =
		isIntersectingBanner &&
		(isIntersectingEndTextBanner || opacity === 1) &&
		!isIntersectingEndBanner

	const isAbsoluteTextBottom = isIntersectingEndBanner

	useEffect(() => {
		if (refTextPlaceHolder.current && refText.current) {
			const textDivHeight = refTextPlaceHolder.current.offsetHeight
			const visibleHeight = Math.min(
				refTextPlaceHolder.current.getBoundingClientRect().height,
				textDivHeight
			)
			const initialOpacity = calculateOpacity(visibleHeight, textDivHeight)
			setOpacity(initialOpacity)
		}

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (
						entry.isIntersecting &&
						refTextPlaceHolder?.current?.offsetHeight
					) {
						const textDivHeight = refTextPlaceHolder?.current?.offsetHeight
						const visibleHeight = Math.min(
							entry.intersectionRect.height,
							textDivHeight
						)
						const visibility = calculateOpacity(visibleHeight, textDivHeight)
						setOpacity(isIntersectingEndBanner ? 1 : visibility)
					}
				}
			},
			{
				threshold: Array.from(
					{ length: ONE_HUNDRED_ONE },
					(_, i) => i / ONE_HUNDRED
				),
			}
		)

		if (ref.current) {
			observer.observe(ref.current)
		}

		return () => {
			if (ref.current) {
				observer.unobserve(ref.current)
			}
		}
	}, [])

	return (
		<div
			ref={ref}
			className={cx(
				styles.root,
				styles.opacityFadeIn,
				applyStylesIf(Boolean(isFixed), styles.fixedText),
				applyStylesIf(Boolean(isAbsoluteTextBottom), styles.normalTextBottom)
			)}
		>
			<div
				className={styles.textPlaceholder}
				ref={refTextPlaceHolder}
				style={
					{
						'--div-height': `${refText?.current?.offsetHeight}px`,
					} as CSSProperties
				}
			/>
			<div
				className={cx(
					isFixed && fixedContentClassName,
					styles.textContainer,
					applyStylesIf(inverse, styles.inverse)
				)}
				style={{ '--text-opacity': opacity } as CSSProperties}
				ref={refText}
			>
				{content}
			</div>
			<div ref={refEndBanner} className={styles.endBanner} />
		</div>
	)
}
